import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageSEO from './PageSEO';

function Pricing() {
  const navigate = useNavigate();
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const handleUpgrade = async (plan) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/register');
      return;
    }
    setUpgradingPlan(plan);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to start checkout');
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      alert(err.message || 'Failed to start checkout');
    } finally {
      setUpgradingPlan(null);
    }
  };

  return (
    <main className="App-main">
      <PageSEO title="Tenant Screening Pricing | $29/Report or $20/mo Pro | Rentual" description="Screen tenants for $29 per report with no subscription. Or unlock the full platform with Pro at $20/month. Compare plans." path="/pricing" />
      <section className="dashboard-section" style={{ maxWidth: '980px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h2 style={{ marginBottom: '0.5rem' }}>Simple pricing</h2>
          <p className="status-text">Pay per screening. Upgrade when you need the full platform.</p>
        </div>

        <div className="pricing-grid" style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>

          {/* Screening */}
          <div className="card" style={{ padding: '1.75rem' }}>
            <p className="status-text" style={{ fontWeight: 700, letterSpacing: '0.02em' }}>Screening</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', margin: '0.75rem 0' }}>
              <span style={{ fontSize: '2rem', fontWeight: 700 }}>$29</span>
              <span className="status-text">/report</span>
            </div>
            <p className="status-text">Quick credit check for any tenant. No subscription required.</p>
            <Link to="/register?intent=screen" className="secondary-button" style={{ display: 'inline-block', marginTop: '1rem' }}>Screen a tenant</Link>
            <ul style={{ marginTop: '1.25rem' }}>
              <li>Credit check</li>
              <li>AI risk assessment</li>
              <li>Self-reported income</li>
              <li>No subscription</li>
              <li>No commitment</li>
            </ul>

          </div>

          {/* Pro Monthly */}
          <div className="card" style={{ padding: '1.75rem', border: '2px solid rgba(40, 182, 200, 0.45)', boxShadow: '0 12px 26px rgba(40, 182, 200, 0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p className="status-text" style={{ fontWeight: 700, letterSpacing: '0.02em' }}>Pro</p>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff', background: 'var(--accent-color, #28b6c8)', padding: '0.25rem 0.6rem', borderRadius: '999px' }}>BEST VALUE</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', margin: '0.75rem 0' }}>
              <span style={{ fontSize: '2rem', fontWeight: 700 }}>$20</span>
              <span className="status-text">/month</span>
            </div>
            <p className="status-text">Full platform. Screenings at $25/report.</p>
            <button
              className="primary-button"
              style={{ display: 'inline-block', marginTop: '1rem' }}
              onClick={() => handleUpgrade('monthly')}
              disabled={upgradingPlan !== null}
            >
              {upgradingPlan === 'monthly' ? 'Starting checkout...' : 'Start Pro'}
            </button>
            <ul style={{ marginTop: '1.25rem' }}>
              <li>Screening at <strong>$25/report</strong></li>
              <li>Verified income (doc uploads)</li>
              <li>Document fraud detection</li>
              <li>Ontario lease generation</li>
              <li>E-signatures</li>
              <li>Rent collection + autopay</li>
              <li>Unlimited properties</li>
              <li>Payment tracking + receipts</li>
              <li>Maintenance requests</li>
            </ul>
          </div>

          {/* Pro Annual */}
          <div className="card" style={{ padding: '1.75rem' }}>
            <p className="status-text" style={{ fontWeight: 700, letterSpacing: '0.02em' }}>Pro Annual</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', margin: '0.75rem 0' }}>
              <span style={{ fontSize: '2rem', fontWeight: 700 }}>$200</span>
              <span className="status-text">/year</span>
            </div>
            <p className="status-text">Save $40. Everything in Pro.</p>
            <button
              className="secondary-button"
              style={{ display: 'inline-block', marginTop: '1rem' }}
              onClick={() => handleUpgrade('annual')}
              disabled={upgradingPlan !== null}
            >
              {upgradingPlan === 'annual' ? 'Starting checkout...' : 'Start annual'}
            </button>
            <ul style={{ marginTop: '1.25rem' }}>
              <li>Everything in Pro</li>
              <li>Save $40/year</li>
              <li>Priority support</li>
            </ul>
          </div>

          {/* Property Managers */}
          <div className="card" style={{ padding: '1.75rem', background: 'var(--surface-color, #f9fafb)' }}>
            <p className="status-text" style={{ fontWeight: 700, letterSpacing: '0.02em' }}>Managing 10+ units?</p>
            <div style={{ margin: '0.75rem 0' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>Property Managers</span>
            </div>
            <p className="status-text">Custom pricing, team access, and portfolio-wide onboarding.</p>
            <Link to="/property-managers" className="secondary-button" style={{ display: 'inline-block', marginTop: '1rem' }}>Learn more</Link>
            <ul style={{ marginTop: '1.25rem' }}>
              <li>Custom plans + volume pricing</li>
              <li>Team access + owner reporting</li>
              <li>Portfolio-wide onboarding</li>
              <li>Dedicated account support</li>
            </ul>
          </div>
        </div>

        <p className="status-text" style={{ marginTop: '1.25rem', textAlign: 'center' }}>
          Subscriptions renew automatically. You can cancel anytime by <Link to="/contact">contacting support</Link>.
        </p>
      </section>
    </main>
  );
}

export default Pricing;
